import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { ProgramMetaForm } from '../components/admin-program/ProgramMetaForm';
import { WeekScheduleEditor } from '../components/admin-program/WeekScheduleEditor';
import { ProgramSidebar } from '../components/admin-program/ProgramSidebar';

const LOGO_URL = '/logo.webp';

export const AdminProgramEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [program, setProgram] = useState(null);
  const [availableWorkouts, setAvailableWorkouts] = useState([]);
  const [stats, setStats] = useState({ enrollments: 0, purchases: 0 });

  const fetchProgram = useCallback(async () => {
    try {
      const response = await api().get(`/admin/programs/${id}/full`);
      setProgram(response.data.program);
      setAvailableWorkouts(response.data.available_workouts);
      setStats({
        enrollments: response.data.enrollment_count,
        purchases: response.data.purchase_count,
      });
    } catch {
      toast.error('Failed to load program');
      navigate('/admin');
    }
    setLoading(false);
  }, [api, id, navigate]);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchProgram();
  }, [fetchProgram, user, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api().put(`/admin/programs/${id}`, program);
      toast.success('Program saved successfully!');
    } catch {
      toast.error('Failed to save program');
    }
    setSaving(false);
  };

  const updateField = (field, value) => setProgram((prev) => ({ ...prev, [field]: value }));

  const updateWeek = (weekIndex, field, value) => {
    setProgram((prev) => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex] = { ...newWeeks[weekIndex], [field]: value };
      return { ...prev, weeks: newWeeks };
    });
  };

  const addWeek = () => {
    const newWeek = {
      week: program.weeks.length + 1,
      focus: 'New Week',
      days: { 0: null, 1: null, 2: null, 3: null, 4: null, 5: null, 6: null },
      rest_days: [6],
    };
    setProgram((prev) => ({
      ...prev,
      weeks: [...prev.weeks, newWeek],
      duration_weeks: prev.weeks.length + 1,
    }));
  };

  const removeWeek = (weekIndex) => {
    if (program.weeks.length <= 1) {
      toast.error('Program must have at least one week');
      return;
    }
    setProgram((prev) => {
      const newWeeks = prev.weeks.filter((_, i) => i !== weekIndex).map((w, i) => ({ ...w, week: i + 1 }));
      return { ...prev, weeks: newWeeks, duration_weeks: newWeeks.length };
    });
  };

  const toggleRestDay = (weekIndex, dayIndex) => {
    setProgram((prev) => {
      const newWeeks = [...prev.weeks];
      const restDays = newWeeks[weekIndex].rest_days || [];
      if (restDays.includes(dayIndex)) {
        newWeeks[weekIndex].rest_days = restDays.filter((d) => d !== dayIndex);
      } else {
        newWeeks[weekIndex].rest_days = [...restDays, dayIndex].sort((a, b) => a - b);
        if (newWeeks[weekIndex].days) newWeeks[weekIndex].days[dayIndex] = null;
      }
      return { ...prev, weeks: newWeeks };
    });
  };

  const assignWorkoutToDay = (weekIndex, dayIndex, workoutId) => {
    setProgram((prev) => {
      const newWeeks = [...prev.weeks];
      if (!newWeeks[weekIndex].days) newWeeks[weekIndex].days = {};
      newWeeks[weekIndex].days[dayIndex] = workoutId || null;
      if (workoutId && newWeeks[weekIndex].rest_days?.includes(dayIndex)) {
        newWeeks[weekIndex].rest_days = newWeeks[weekIndex].rest_days.filter((d) => d !== dayIndex);
      }
      return { ...prev, weeks: newWeeks };
    });
  };

  if (loading || !program) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-program-editor">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-primary transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">Back to Admin</span>
            </Link>
          </div>
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          <Button
            onClick={handleSave}
            className="bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
            disabled={saving}
            data-testid="save-program-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <ProgramMetaForm program={program} updateField={updateField} />
            <WeekScheduleEditor
              program={program}
              availableWorkouts={availableWorkouts}
              onAddWeek={addWeek}
              onRemoveWeek={removeWeek}
              onUpdateWeek={updateWeek}
              onToggleRestDay={toggleRestDay}
              onAssignWorkoutToDay={assignWorkoutToDay}
            />
          </div>

          <ProgramSidebar
            program={program}
            availableWorkouts={availableWorkouts}
            stats={stats}
            programId={id}
          />
        </div>
      </main>
    </div>
  );
};
